import { useEffect, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { Button, Dialog, TextField } from '@cere/cere-design-system';
import '@/src/styles/orgs.css';

/**
 * Advanced Expansion (memory tier 2, the Bloom pattern): one node's
 * neighbourhood read with explicit discipline — an edge-type filter, a
 * direction, and a NODE BUDGET (the client cap per expansion). Purely
 * presentational: the section owns the read, the refusal doctrine and the
 * merge; this dialog only shapes the params.
 *
 * The EDGE TYPE vocabulary is RELATION TYPES OBSERVED IN THE LOADED
 * neighbours rows — a client-side vocabulary, never a claim that the server
 * enumerates edge types (there is no such read). An empty choice asks for
 * every edge type.
 *
 * `direction: 'both'` is the default and maps to an ABSENT wire key (the
 * server's own default) — the tier-1 omitempty discipline: unset axes are
 * missing keys, never `''`/`'both'` stamped client-side.
 */

/** The budget's bounds: default 25, hard client max 100. The value rides the
 *  SDK `limit` param as the TIGHTER client cap — the server independently
 *  clamps limits to its own 50-default/500-max rule, so a budget in [1,100]
 *  always survives the round trip as given. */
export const DEFAULT_EXPANSION_BUDGET = 25;
export const MAX_EXPANSION_BUDGET = 100;

export type ExpansionDirection = 'in' | 'out' | 'both';

/** What an expansion asks for — the dialog's output, the section's input. */
export interface AdvancedExpansion {
  edgeType: string | null;
  direction: ExpansionDirection;
  budget: number;
}

interface Props {
  open: boolean;
  /** The node the expansion targets (`null` with `open` false). */
  node: { id: string; title: string | null } | null;
  /** Relation types observed in the loaded neighbours rows — the only
   *  vocabulary the select offers. */
  edgeTypes: string[];
  /** The standing budget the form opens with (the last one used). */
  defaultBudget: number;
  onExpand: (expansion: AdvancedExpansion) => void;
  onClose: () => void;
}

export function MemoryExpandDialog({ open, node, edgeTypes, defaultBudget, onExpand, onClose }: Props) {
  const [edgeType, setEdgeType] = useState('');
  const [direction, setDirection] = useState<ExpansionDirection>('both');
  const [budgetInput, setBudgetInput] = useState(String(defaultBudget));

  // A reopen (or a different node) starts the form over: the filters are
  // per-expansion choices, not sticky panel state — only the BUDGET carries
  // forward via `defaultBudget`, because it is the standing client cap.
  useEffect(() => {
    if (open) {
      setEdgeType('');
      setDirection('both');
      setBudgetInput(String(defaultBudget));
    }
  }, [open, node?.id, defaultBudget]);

  function submit() {
    const parsed = Number.parseInt(budgetInput, 10);
    const budget = Number.isNaN(parsed) ? DEFAULT_EXPANSION_BUDGET : Math.min(MAX_EXPANSION_BUDGET, Math.max(1, parsed));
    onExpand({ edgeType: edgeType === '' ? null : edgeType, direction, budget });
    onClose();
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title="Advanced expansion"
      onClose={onClose}
      customActions={
        <>
          <Button variant="tertiary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit}>Expand</Button>
        </>
      }
    >
      <div className="vault-orgs__memory-expand-body" data-testid="memory-expand-dialog">
        <p className="vault-orgs__note">
          {`Add ${node?.title || node?.id || 'this record'}’s neighbourhood — filtered and capped as you choose.`}
        </p>
        <FormControl fullWidth size="small">
          <InputLabel id="memory-expand-edge-label">Edge type</InputLabel>
          <Select
            labelId="memory-expand-edge-label"
            label="Edge type"
            value={edgeType}
            onChange={(e) => setEdgeType(String(e.target.value))}
          >
            <MenuItem value="">Any edge type</MenuItem>
            {edgeTypes.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel id="memory-expand-direction-label">Direction</InputLabel>
          <Select
            labelId="memory-expand-direction-label"
            label="Direction"
            value={direction}
            onChange={(e) => setDirection(e.target.value as ExpansionDirection)}
          >
            <MenuItem value="both">Both directions</MenuItem>
            <MenuItem value="out">Outgoing — this record → others</MenuItem>
            <MenuItem value="in">Incoming — others → this record</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Node budget"
          value={budgetInput}
          inputProps={{ min: 1, max: MAX_EXPANSION_BUDGET }}
          onChange={(e) => setBudgetInput(e.target.value)}
          helperText={`Up to ${MAX_EXPANSION_BUDGET} records per expansion (default ${DEFAULT_EXPANSION_BUDGET}).`}
        />
      </div>
    </Dialog>
  );
}
